<template>
    <div
        id="test-modal-open"
        class="kgr-modal"
        :class="{'is-open': value}"
    >
        <div
            ref="dialog"
            class="kgr-modal__dialog"
            :style="combinedDialogStyles"
        >
            <div class="kgr-modal__header kgr-region kgr-region--accent">
                <div class="kgr-modal__headerContent">
                    <h1
                        v-if="headline"
                        class="kgr-headline kgr-headline--h3"
                    >
                        {{ headline }}
                    </h1>
                </div>
                <button
                    class="kgr-modal__headerClose kgr-clickable kgr-clickable--touchy"
                    aria-disabled="true"
                    @click="closeModal"
                >
                    <kgr-icon-close />
                </button>
            </div>
            <div
                ref="content"
                class="kgr-modal__content kgr-region kgr-region--default"
                :style="combinedContentStyles"
            >
                <slot></slot>
            </div>
            <div
                v-if="$slots.footer"
                :style="backgroundColorStyle"
                class="kgr-modal__footer kgr-region kgr-text kgr-text--rightAligned"
            >
                <slot name="footer"></slot>
            </div>
            <div
                v-if="resizable"
                class="resizeHandle"
                @mousedown.prevent="startResize"
                @touchstart.prevent="startResize"
            ></div>
        </div>
    </div>
</template>

<script>
import IconClose from '../icon/IconClose';

export default {
    components: {
        'kgr-icon-close': IconClose,
    },
    props: {
        value: {
            type: Boolean,
            required: true,
        },
        headline: {
            type: String,
            required: false,
        },
        widthInPx: {
            type: Number,
            required: false,
            default: null,
        },
        scrollable: {
            type: Boolean,
            required: false,
            default: false,
        },
        resizable: {
            type: Boolean,
            required: false,
            default: false,
        },
        height: {
            type: String,
            required: false,
            default: null,
        },
        backgroundColor: {
            type: String,
            required: false,
            default: null,
        },
        dialogStyles: {
            type: Object,
            required: false,
            default: () => ({})
        },
        contentStyles: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },
    data() {
        return {
            isResizing: false,
            resizeStartX: 0,
            resizeStartY: 0,
            startWidth: 0,
            startHeight: 0,
            currentWidth: null,
            currentHeight: null,
        };
    },
    computed: {
        combinedDialogStyles() {
            const combined = { ...(this.dialogStyles || {}) }

            if (this.currentWidth) {
                combined.width = this.currentWidth + 'px';
            } else if (this.widthInPx) {
                combined.width = this.widthInPx + 'px';
            }

            if (this.currentHeight) {
                combined.height = this.currentHeight + 'px';
            } else if(this.height) {
                combined.height = this.height;
            }

            return combined;
        },
        combinedContentStyles() {
            const combined = {
                ...(this.contentStyles || {}),
                backgroundColor: this.backgroundColor
            }

            if (this.scrollable) {
                combined.overflowY = 'auto';
                combined.maxHeight = '100vh';
            }

            return combined;
        },
        backgroundColorStyle() {
            return this.backgroundColor ? 'background-color: ' + this.backgroundColor + ';' : '';
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (value) {
                    document.body.style.setProperty('--kgr-actionButton-floating-z-index', 0)
                    document.body.style.setProperty('overflow', 'hidden');
                    document.querySelector('.app-pageLayout__header').style.zIndex = 2;

                    if (this.resizable) {
                        this.$nextTick(() => this.syncSizeFromDialog());
                    }
                } else {
                    this.stopResize();
                    this.reset();
                }
            }
        }
    },
    created() {
        document.addEventListener('keyup', this.escapeHandler);
    },
    beforeUnmount() {
        this.reset();
        this.stopResize(true);
        document.removeEventListener('keyup', this.escapeHandler)
    },
    mounted() {
        $('.ui-tooltip').remove();
    },
    methods: {
        escapeHandler(e) {
            if (e.keyCode === 27) {
                this.closeModal();
            }
        },
        closeModal($event) {
            this.$emit('input', false);
            this.$nextTick(() => {
                this.$emit('post-input', false);
            });
            this.$emit('close', $event);
        },
        reset() { // we should avoid modifying the DOM directly, but it is hard to avoid with vue 2
            document.querySelector('.app-pageLayout__header').style.zIndex = 3;
            document.body.style.removeProperty('--kgr-actionButton-floating-z-index')
            document.body.style.removeProperty('overflow');
        },
        scrollTo(element, offset) {
            const modalContentElement = this.$refs.content;
            const elementTop = element.getBoundingClientRect().top - modalContentElement.getBoundingClientRect().top + modalContentElement.scrollTop;
            this.$refs.content.scrollTo({
                top: elementTop - (offset || 0),
                behavior: 'smooth'
            });
        },
        startResize(event) {
            const dialog = this.$refs.dialog;
            const position = this.getClientPosition(event);

            if (!dialog || !position) {
                return;
            }

            this.isResizing = true;
            this.resizeStartX = position.x;
            this.resizeStartY = position.y;
            this.startWidth = dialog.getBoundingClientRect().width;
            this.startHeight = dialog.getBoundingClientRect().height;

            this.addResizeListeners();
        },
        onResize(event) {
            if (!this.isResizing) {
                return;
            }

            if (event.preventDefault) {
                event.preventDefault(); // keep dragging smooth, avoid text selection/scroll
            }

            const position = this.getClientPosition(event);

            if (!position) {
                return;
            }

            const deltaX = position.x - this.resizeStartX;
            const deltaY = position.y - this.resizeStartY;

            const minWidth = 260;
            const minHeight = 200;
            const margin = 16; // matches modal margin on large screens
            const maxWidth = Math.max(minWidth, window.innerWidth - (margin * 2));
            const maxHeight = Math.max(minHeight, window.innerHeight - (margin * 2));

            this.currentWidth = Math.min(Math.max(this.startWidth + deltaX * 2, minWidth), maxWidth);
            this.currentHeight = Math.min(Math.max(this.startHeight + deltaY * 2, minHeight), maxHeight);
        },
        stopResize(force) {
            if (!this.isResizing && !force) {
                return;
            }

            this.isResizing = false;
            this.removeResizeListeners();
        },
        addResizeListeners() {
            window.addEventListener('mousemove', this.onResize, { passive: false });
            window.addEventListener('mouseup', this.stopResize, { passive: true });
            window.addEventListener('touchmove', this.onResize, { passive: false });
            window.addEventListener('touchend', this.stopResize, { passive: true });
        },
        removeResizeListeners() {
            window.removeEventListener('mousemove', this.onResize);
            window.removeEventListener('mouseup', this.stopResize);
            window.removeEventListener('touchmove', this.onResize);
            window.removeEventListener('touchend', this.stopResize);
        },
        getClientPosition(event) {
            if (event.touches && event.touches.length) {
                return {
                    x: event.touches[0].clientX,
                    y: event.touches[0].clientY
                };
            }

            if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
                return {
                    x: event.clientX,
                    y: event.clientY
                };
            }

            return null;
        },
        syncSizeFromDialog() {
            const dialog = this.$refs.dialog;

            if (!dialog) {
                return;
            }

            this.currentWidth = dialog.getBoundingClientRect().width;
            this.currentHeight = dialog.getBoundingClientRect().height;
        }
    }
}
</script>

<style lang="scss" scoped>
.resizeHandle {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 1.5rem;
    height: 1.5rem;
    cursor: se-resize;
    pointer-events: all;
    touch-action: none;

    &::after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, transparent 55%, rgba(0, 0, 0, 0.25) 55%);
    }
}
</style>
